<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import differenceInDays from 'date-fns/differenceInDays';
import FilterBar from 'dashboard/components-next/filter-bar/FilterBar.vue';
import FilterPill from 'dashboard/components-next/filter-bar/FilterPill.vue';
import PeriodPicker from 'dashboard/components-next/filter-bar/PeriodPicker.vue';
import { periodFromRange } from 'dashboard/components-next/filter-bar/period';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { GROUP_BY_FILTER } from '../constants';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import {
  generateReportURLParams,
  hasBusinessHoursInboxes,
  parseReportURLParams,
} from '../helpers/reportFilterHelper';

const props = defineProps({
  filterType: {
    type: String,
    required: false,
    default: '',
    validator: value =>
      ['teams', 'inboxes', 'labels', 'agents', ''].includes(value),
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  showGroupBy: {
    type: Boolean,
    default: true,
  },
  showBusinessHours: {
    type: Boolean,
    default: true,
  },
  showEntityFilter: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const buildReportFilterList = items => {
  if (!Array.isArray(items)) return [];

  return items.map(item => ({
    id: item.id,
    name: item.name || item.title,
  }));
};

const getReportFilterKey = filterType => {
  const keyMap = {
    teams: 'team_id',
    inboxes: 'inbox_id',
    labels: 'label_id',
    agents: 'agent_id',
  };
  return keyMap[filterType] || '';
};

const getFilterKey = () => getReportFilterKey(props.filterType);

// UI-FILTROS-01 (UF-44): o período do PeriodPicker, `{ range, from, to }`, com `from` e `to` em
// segundos, do início ao fim do dia. Sem período na URL, os últimos 7 dias, como antes.
const period = ref(periodFromRange(DATE_RANGE_TYPES.LAST_7_DAYS));
const businessHoursSelected = ref(false);
const groupBy = ref(GROUP_BY_FILTER[1]);
const groupByfilterItemsList = ref([{ id: 1, name: 'Day' }]);

const appliedFilters = ref(
  props.showEntityFilter
    ? { [getFilterKey()]: props.selectedItem?.id || null }
    : {}
);

const filterSource = computed(() => {
  const sources = {
    teams: store.getters['teams/getTeams'],
    inboxes: store.getters['inboxes/getInboxes'],
    labels: store.getters['labels/getLabels'],
    agents: store.getters['agents/getAgents'],
  };
  return sources[props.filterType] || [];
});

// O interruptor só existe quando alguma caixa do recorte tem horário comercial.
// Fora disso ele some e vale desligado, mesmo que a URL traga business_hours.
const isBusinessHoursAvailable = computed(
  () =>
    props.showBusinessHours &&
    hasBusinessHoursInboxes(
      store.getters['inboxes/getInboxes'],
      props.filterType === 'inboxes' ? appliedFilters.value.inbox_id : null
    )
);

const businessHours = computed(
  () => isBusinessHoursAvailable.value && businessHoursSelected.value
);

const from = computed(() => period.value.from);
const to = computed(() => period.value.to);

const daysDifference = computed(() => {
  return differenceInDays(
    new Date(to.value * 1000),
    new Date(from.value * 1000)
  );
});

const isGroupByPossible = computed(() => {
  return props.showGroupBy && daysDifference.value >= 29;
});

const GROUP_BY_OPTIONS = computed(() => ({
  WEEK: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  ],
  MONTH: [
    { id: 1, name: t('REPORT.GROUPING_OPTIONS.DAY') },
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
  ],
  YEAR: [
    { id: 2, name: t('REPORT.GROUPING_OPTIONS.WEEK') },
    { id: 3, name: t('REPORT.GROUPING_OPTIONS.MONTH') },
    { id: 4, name: t('REPORT.GROUPING_OPTIONS.YEAR') },
  ],
}));

// `daysDifference` conta um a menos que os dias do período: 30 dias dão 29, e o
// chip de 90 dias dá 89.
const fetchFilterItems = () => {
  const days = daysDifference.value;
  if (days >= 364) return GROUP_BY_OPTIONS.value.YEAR;
  if (days >= 89) return GROUP_BY_OPTIONS.value.MONTH;
  if (days >= 29) return GROUP_BY_OPTIONS.value.WEEK;
  return GROUP_BY_OPTIONS.value.WEEK;
};

const filterOptions = computed(() => buildReportFilterList(filterSource.value));

// A pílula da barra padrão (FilterBar): `single` e `scalar`, porque a API
// recebe um id só (UI-FILTROS-01 PLAN §8).
const fieldLabel = computed(() => {
  const labels = {
    teams: t('TEAM_REPORTS.FILTERS.FIELD'),
    inboxes: t('INBOX_REPORTS.FILTERS.FIELD'),
    labels: t('LABEL_REPORTS.FILTERS.FIELD'),
    agents: t('AGENT_REPORTS.FILTERS.FIELD'),
  };
  return labels[props.filterType];
});

const fields = computed(() =>
  props.showEntityFilter && fieldLabel.value
    ? [
        {
          key: 'entity',
          label: fieldLabel.value,
          options: filterOptions.value,
          single: true,
          scalar: true,
        },
      ]
    : []
);

const barModel = computed(() => ({
  entity: appliedFilters.value[getFilterKey()] ?? null,
}));

const updateURLParams = () => {
  const params = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
    groupBy: isGroupByPossible.value ? groupBy.value.id : null,
    range: period.value.range,
  });

  router.replace({ query: { ...params } });
};

const emitChange = () => {
  const payload = {
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  };

  if (props.showGroupBy) {
    // Always emit groupBy, default to day when range is too short
    payload.groupBy = isGroupByPossible.value
      ? groupBy.value
      : GROUP_BY_FILTER[1];
  }

  if (props.showEntityFilter) {
    const filterKey = getFilterKey();
    const selectedValue = appliedFilters.value[filterKey];

    if (selectedValue) {
      payload[props.filterType] =
        props.filterType === 'agents'
          ? [{ id: selectedValue }]
          : { id: selectedValue };
    }
  }

  updateURLParams();
  emit('filterChange', payload);
};

// Escolher na pílula troca de página; tirar (o ✕ ou "Limpar filtros") volta à
// página sem nenhum escolhido. Na etiqueta, é a visão geral das etiquetas: a
// página `label_reports` não busca nada sem etiqueta e mostraria os números da
// que acabou de sair, que seguem na store.
const selectFilter = id => {
  const filterKey = getFilterKey();
  appliedFilters.value[filterKey] = id;
  emitChange();

  // Time, caixa e atendente abrem a página Atendimento já filtrada (REPORTS-02),
  // como as rotas de antes; a etiqueta segue na página dela.
  const atendimentoFilterMap = {
    teams: 'team_id',
    inboxes: 'inbox_id',
    agents: 'agent_id',
  };
  const atendimentoFilter = atendimentoFilterMap[props.filterType];
  if (atendimentoFilter) {
    const { range, from: since, to: until } = route.query;
    router.push({
      name: 'account_atendimento_reports',
      params: { accountId: route.params.accountId },
      query: {
        range,
        from: since,
        to: until,
        [atendimentoFilter]: id ?? undefined,
      },
    });
  } else if (props.filterType === 'labels') {
    router.push(
      id
        ? {
            name: 'label_reports_show',
            params: { ...route.params, id },
            query: route.query,
          }
        : {
            name: 'label_reports_index',
            params: { accountId: route.params.accountId },
            query: route.query,
          }
    );
  }
};

// As opções de "agrupar por" são as do período. O agrupamento que não está entre
// elas (trocado o período, ou vindo de um link) vira o primeiro delas.
const syncGroupByOptions = () => {
  groupByfilterItemsList.value = fetchFilterItems();
  const filterItems = groupByfilterItemsList.value.filter(
    item => item.id === groupBy.value.id
  );
  if (filterItems.length === 0) {
    groupBy.value = GROUP_BY_FILTER[groupByfilterItemsList.value[0].id];
  }
};

const onPeriodChange = value => {
  period.value = value;
  syncGroupByOptions();
  emitChange();
};

const onBusinessHoursToggle = () => {
  emitChange();
};

const onGroupByFilterChange = ([id]) => {
  groupBy.value = GROUP_BY_FILTER[id];
  emitChange();
};

const initializeFromURL = () => {
  const urlParams = parseReportURLParams(route.query);

  // Com nome, o período da URL sempre traz as datas (`resolvePeriod`), levadas ao
  // início e ao fim do dia, como o PeriodPicker as quer.
  if (urlParams.range) {
    period.value = {
      range: urlParams.range,
      from: getUnixStartOfDay(new Date(urlParams.from * 1000)),
      to: getUnixEndOfDay(new Date(urlParams.to * 1000)),
    };
  }

  if (urlParams.businessHours) {
    businessHoursSelected.value = urlParams.businessHours;
  }

  if (urlParams.groupBy) {
    const groupByValue = GROUP_BY_FILTER[urlParams.groupBy];
    if (groupByValue) {
      groupBy.value = groupByValue;
    }
  }

  // Initialize entity filter from route params (not URL query)
  if (props.showEntityFilter && route.params.id) {
    const filterKey = getFilterKey();
    appliedFilters.value[filterKey] = Number(route.params.id);
  }
};

onMounted(() => {
  initializeFromURL();
  syncGroupByOptions();
  emitChange();
});

// Aberta direto pelo link, a tela monta antes de as caixas chegarem: quando o
// interruptor aparece (ou some), o business_hours da URL passa a valer (ou não).
watch(isBusinessHoursAvailable, () => {
  if (businessHoursSelected.value) emitChange();
});
</script>

<template>
  <FilterBar
    :model-value="barModel"
    :fields="fields"
    :search="false"
    @update:model-value="value => selectFilter(value.entity)"
    @reset="selectFilter(null)"
  >
    <template #period>
      <PeriodPicker
        :model-value="period"
        @update:model-value="onPeriodChange"
      />
    </template>
    <!-- "Agrupar por" não filtra: fica fora da contagem e do "Limpar filtros", e
         à vista no celular, como o período. -->
    <template #toggles>
      <FilterPill
        v-if="isGroupByPossible"
        :label="$t('REPORT.GROUP_BY_FILTER_DROPDOWN_LABEL')"
        :options="groupByfilterItemsList"
        single
        :clearable="false"
        :model-value="[groupBy.id]"
        @update:model-value="onGroupByFilterChange"
      />
      <label
        v-if="isBusinessHoursAvailable"
        class="flex gap-2 items-center m-0 text-sm font-normal leading-normal cursor-pointer text-n-slate-11"
      >
        <ToggleSwitch
          v-model="businessHoursSelected"
          @change="onBusinessHoursToggle"
        />
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </label>
    </template>
  </FilterBar>
</template>
